import Box from '@mui/material/Box';
import debounce from 'lodash/debounce';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import ControlledTextField from '../../BasicUI/ControlledInputs/ControlledTextField';
import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

// .field — @apply --marketplaceDefaultFontStyles; margin: 0; padding: 1px 0 13px 0; border: none;
const fieldSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  margin: 0,
  padding: '1px 0 13px 0',
  border: 'none',
});

// .fieldPlain — @apply --marketplaceDefaultFontStyles; margin: 0; border: none;
const fieldPlainSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  margin: 0,
  border: 'none',
});

// When user types, we wait for new keystrokes a while before searching new content
const DEBOUNCE_WAIT_TIME = 600;
// Short search queries (e.g. 2 letters) have a longer timeout before search is made
const TIMEOUT_FOR_SHORT_QUERIES = 2000;

const getKeywordQueryParam = (queryParamNames) => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'keywords';
};

// RHF child rendered inside FilterForm's <FormProvider>. Reads the form control
// from context and writes into `name` (string value) exactly like the previous
// Final-Form <FieldTextInput name={name} type="text" />.
const KeywordInput = ({ id, name, label, placeholder, sx, inputRef }) => {
  const { control } = useFormContext();
  return (
    <ControlledTextField
      control={control}
      fullWidth
      label={label}
      name={name}
      placeholder={placeholder}
      textFieldProps={{
        autoComplete: 'off',
        inputRef,
        sx,
      }}
    />
  );
};

/**
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} props.id
 * @param {string} props.name
 * @param {React.ReactNode} props.label
 * @param {Object} [props.initialValues]
 * @param {number} [props.contentPlacementOffset]
 * @param {Function} props.onSubmit
 * @param {string|string[]} props.queryParamNames
 * @param {boolean} [props.showAsPopup]
 * @param {Function} [props.applyFilters]
 * @param {Object} [props.filterConfig]
 */
const KeywordFilterComponent = ({
  rootClassName = null,
  className = null,
  id,
  name,
  label,
  initialValues = null,
  contentPlacementOffset = 0,
  onSubmit,
  queryParamNames,
  showAsPopup = false,
  applyFilters,
  filterConfig = {},
  ...rest
}) => {
  const intl = useIntl();

  const filterRef = useRef(null);
  const filterContentRef = useRef(null);
  const shortKeywordTimeoutRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(shortKeywordTimeoutRef.current);
    };
  }, []);

  const positionStyleForContent = useCallback(() => {
    if (filterRef.current && filterContentRef.current) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - filterRef.current.getBoundingClientRect().right;
      const labelWidth = filterRef.current.offsetWidth;
      const contentWidth = filterContentRef.current.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  const urlParam = getKeywordQueryParam(queryParamNames);
  const hasInitialValues =
    !!initialValues && !!initialValues[urlParam] && initialValues[urlParam].length > 0;
  const labelForPopup = hasInitialValues
    ? intl.formatMessage({ id: 'KeywordFilter.labelSelected' }, { labelText: initialValues[urlParam] })
    : label;

  const labelForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'KeywordFilterPlainForm.labelSelected' },
        { labelText: initialValues[urlParam] }
      )
    : label;

  const filterText = intl.formatMessage({ id: 'KeywordFilter.filterText' });

  const placeholder = intl.formatMessage({ id: 'KeywordFilter.placeholder' });

  const contentStyle = positionStyleForContent();

  // pass the initial values with the name key so that
  // they can be passed to the correct field. RHF needs a defined default
  // (empty string) rather than undefined to keep the input controlled.
  const namedInitialValues = { [name]: initialValues[urlParam] || '' };

  const handleSubmit = (values) => {
    const usedValue = values ? values[name] : values;
    onSubmit({ [urlParam]: usedValue });
  };

  const debouncedSubmit = debounce(handleSubmit, DEBOUNCE_WAIT_TIME, {
    leading: false,
    trailing: true,
  });
  // Use timeout for shart queries and debounce for queries with any length
  const handleChangeWithDebounce = (values) => {
    // handleSubmit gets values as params.
    // If this field ("keyword") is short, create timeout
    const hasKeywordValue = values && values[name];
    const keywordValue = hasKeywordValue ? values && values[name] : '';
    if (!hasKeywordValue || (hasKeywordValue && keywordValue.length >= 3)) {
      if (shortKeywordTimeoutRef.current) {
        window.clearTimeout(shortKeywordTimeoutRef.current);
      }
      return debouncedSubmit(values);
    } else {
      shortKeywordTimeoutRef.current = window.setTimeout(() => {
        // if mobileInputRef exists, use the most up-to-date value from there
        return mobileInputRef.current
          ? handleSubmit({ ...values, [name]: mobileInputRef.current.value })
          : handleSubmit(values);
      }, TIMEOUT_FOR_SHORT_QUERIES);
    }
  };

  // Clear the underlying DOM input through the reference, mirroring the prior
  // uncontrolled-field clear behavior.
  const handleClear = () => {
    if (mobileInputRef.current) {
      mobileInputRef.current.value = '';
    }
  };

  return showAsPopup ? (
    <FilterPopup
      applyFilters={applyFilters}
      className={className}
      contentPlacementOffset={contentPlacementOffset}
      id={`${id}.popup`}
      initialValues={namedInitialValues}
      isSelected={hasInitialValues}
      keepDirtyOnReinitialize
      label={labelForPopup}
      labelMaxWidth={250}
      name={name}
      onSubmit={handleSubmit}
      rootClassName={rootClassName}
      showAsPopup={showAsPopup}
      {...rest}
    >
      <KeywordInput
        id={`${id}-input`}
        label={filterText}
        name={name}
        placeholder={placeholder}
        sx={fieldSx}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      contentPlacementOffset={contentStyle}
      id={`${id}.plain`}
      initialValues={namedInitialValues}
      isSelected={hasInitialValues}
      label={labelForPlain}
      liveEdit
      onClear={handleClear}
      onSubmit={handleChangeWithDebounce}
      rootClassName={rootClassName}
      {...rest}
    >
      <Box component="fieldset" sx={fieldPlainSx}>
        <KeywordInput
          id={`${id}-input`}
          inputRef={mobileInputRef}
          name={name}
          placeholder={placeholder}
        />
      </Box>
    </FilterPlain>
  );
};

const KeywordFilter = memo(KeywordFilterComponent);

export default KeywordFilter;
